import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, Check, X, Star, MapPin, Phone, Mail, Calendar,
  Car, Home, Truck, Sofa, Fuel, Gauge, Bed, Bath, Ruler, Users,
  Tag, Building, DollarSign, Loader2, ArrowRight, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type configurations
const TYPE_CONFIG = {
  vehicles: {
    name: "Vehicle",
    namePlural: "Vehicles",
    color: "#EF4444",
    icon: Car,
    specs: [
      { key: "year", label: "Year" },
      { key: "make", label: "Make" },
      { key: "model", label: "Model" },
      { key: "mileage", label: "Mileage", format: (v) => v ? `${v.toLocaleString()} mi` : "N/A" },
      { key: "condition", label: "Condition" },
      { key: "body_style", label: "Body Style" },
      { key: "transmission", label: "Transmission" },
      { key: "drivetrain", label: "Drivetrain" },
      { key: "fuel_type", label: "Fuel Type" },
      { key: "exterior_color", label: "Exterior Color" },
    ],
  },
  real_estate: {
    name: "Property",
    namePlural: "Properties",
    color: "#EC4899",
    icon: Home,
    specs: [
      { key: "property_type", label: "Property Type" },
      { key: "listing_status", label: "Status", format: (v) => v?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) },
      { key: "bedrooms", label: "Bedrooms" },
      { key: "bathrooms", label: "Bathrooms" },
      { key: "sqft", label: "Square Feet", format: (v) => v ? v.toLocaleString() : "N/A" },
      { key: "lot_size", label: "Lot Size", format: (v) => v ? `${v} acres` : "N/A" },
      { key: "year_built", label: "Year Built" },
      { key: "address", label: "Address" },
    ],
  },
  rvs: {
    name: "RV",
    namePlural: "RVs",
    color: "#06B6D4",
    icon: Truck,
    specs: [
      { key: "year", label: "Year" },
      { key: "make", label: "Make" },
      { key: "rv_type", label: "RV Type" },
      { key: "condition", label: "Condition" },
      { key: "sleeps", label: "Sleeps" },
      { key: "length", label: "Length", format: (v) => v ? `${v} ft` : "N/A" },
      { key: "slideouts", label: "Slide-outs" },
      { key: "mileage", label: "Mileage", format: (v) => v ? `${v.toLocaleString()} mi` : "N/A" },
    ],
  },
  furniture: {
    name: "Furniture",
    namePlural: "Furniture",
    color: "#8B5CF6",
    icon: Sofa,
    specs: [
      { key: "category", label: "Category" },
      { key: "subcategory", label: "Subcategory" },
      { key: "style", label: "Style" },
      { key: "material", label: "Material" },
      { key: "room_type", label: "Room Type" },
      { key: "condition", label: "Condition" },
    ],
  },
};

export default function ComparePage() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.vehicles;
  const TypeIcon = config.icon;
  
  useEffect(() => {
    const fetchItems = async () => {
      const ids = searchParams.get("ids");
      if (!ids) {
        setError("No items selected for comparison");
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/marketplace/compare`, {
          params: { listing_type: type, ids }
        });
        setItems(response.data);
      } catch (err) {
        console.error("Error fetching comparison items:", err);
        setError("Failed to load comparison items");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchItems();
    window.scrollTo(0, 0);
  }, [type, searchParams]);
  
  if (isLoading) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: config.color }} />
      </div>
    );
  }
  
  if (error || items.length === 0) {
    return (
      <div className="page-container min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <TypeIcon className="w-20 h-20 mx-auto mb-6 text-[#64748B]" />
          <h1 className="text-3xl font-bold text-white mb-4">{error || "No Items to Compare"}</h1>
          <p className="text-[#94A3B8] mb-8">
            Select at least 2 items from the marketplace to compare them.
          </p>
          <Link to={`/marketplace/${type}`}>
            <Button style={{ backgroundColor: config.color }}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to {config.namePlural}
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const getItemTitle = (item) => {
    if (type === "vehicles") {
      return `${item.year} ${item.make} ${item.model}`;
    }
    return item.title;
  };
  
  return (
    <div className="page-container min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#0F172A] to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link 
            to={`/marketplace/${type}`}
            className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to {config.namePlural}
          </Link>
          
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <TypeIcon className="w-7 h-7" style={{ color: config.color }} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                Compare {config.namePlural}
              </h1>
              <p className="text-[#94A3B8]">
                Side-by-side comparison of {items.length} {items.length === 1 ? config.name.toLowerCase() : config.namePlural.toLowerCase()}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Comparison Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            {/* Images Row */}
            <thead>
              <tr>
                <th className="w-48 p-4 text-left text-[#64748B] font-medium border-b border-white/10">
                  {/* Empty cell for labels column */}
                </th>
                {items.map((item, idx) => (
                  <th key={item.id} className="p-4 border-b border-white/10">
                    <div className="glass-card rounded-xl overflow-hidden">
                      <div className="aspect-[16/10] bg-[#1E293B] relative">
                        <img
                          src={item.images?.[0] || "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?auto=compress&cs=tinysrgb&w=800"}
                          alt={getItemTitle(item)}
                          className="w-full h-full object-cover"
                        />
                        {item.featured && (
                          <Badge className="absolute top-2 left-2 bg-[#7C3AED]">Featured</Badge>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-bold text-lg mb-1 line-clamp-2">
                          {getItemTitle(item)}
                        </h3>
                        <p className="text-2xl font-bold" style={{ color: config.color }}>
                          {item.price ? `$${item.price.toLocaleString()}` : "Call for Price"}
                        </p>
                        {(item.city || item.state) && (
                          <p className="text-sm text-[#64748B] flex items-center gap-1 mt-2">
                            <MapPin className="w-3 h-3" />
                            {[item.city, item.state].filter(Boolean).join(", ")}
                          </p>
                        )}
                        <Link to={`/marketplace/${type}/${item.id}`}>
                          <Button 
                            className="w-full mt-4"
                            style={{ backgroundColor: config.color }}
                          >
                            View Details <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            
            {/* Specs Rows */}
            <tbody>
              {/* Price Row */}
              <tr className="bg-white/5">
                <td className="p-4 text-[#94A3B8] font-medium border-b border-white/10">
                  <DollarSign className="w-4 h-4 inline mr-2" style={{ color: config.color }} />
                  Price
                </td>
                {items.map((item) => (
                  <td key={item.id} className="p-4 text-center border-b border-white/10">
                    <span className="text-white font-bold text-lg">
                      {item.price ? `$${item.price.toLocaleString()}` : "Call"}
                    </span>
                  </td>
                ))}
              </tr>
              
              {/* Dynamic Specs */}
              {config.specs.map((spec, idx) => (
                <tr key={spec.key} className={idx % 2 === 0 ? "" : "bg-white/5"}>
                  <td className="p-4 text-[#94A3B8] font-medium border-b border-white/10">
                    {spec.label}
                  </td>
                  {items.map((item) => {
                    const value = item[spec.key];
                    const displayValue = spec.format ? spec.format(value) : value;
                    return (
                      <td key={item.id} className="p-4 text-center border-b border-white/10">
                        <span className="text-white">
                          {displayValue || <span className="text-[#64748B]">—</span>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              
              {/* Location Row */}
              <tr className="bg-white/5">
                <td className="p-4 text-[#94A3B8] font-medium border-b border-white/10">
                  <MapPin className="w-4 h-4 inline mr-2" style={{ color: config.color }} />
                  Location
                </td>
                {items.map((item) => (
                  <td key={item.id} className="p-4 text-center border-b border-white/10">
                    <span className="text-white">
                      {[item.city, item.state].filter(Boolean).join(", ") || "—"}
                    </span>
                  </td>
                ))}
              </tr>
              
              {/* Contact Row */}
              <tr>
                <td className="p-4 text-[#94A3B8] font-medium">
                  <Phone className="w-4 h-4 inline mr-2" style={{ color: config.color }} />
                  Contact
                </td>
                {items.map((item) => (
                  <td key={item.id} className="p-4 text-center">
                    <div className="space-y-2">
                      {item.contact_phone && (
                        <a 
                          href={`tel:${item.contact_phone}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors"
                        >
                          <Phone className="w-4 h-4" style={{ color: config.color }} />
                          {item.contact_phone}
                        </a>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Share Comparison */}
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              alert("Comparison link copied to clipboard!");
            }}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share This Comparison
          </Button>
        </div>
      </div>
    </div>
  );
}
